import { motion } from "framer-motion";
import { Film, Clock3, TrendingUp, Handshake } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const icons = [Film, Clock3, TrendingUp, Handshake];

const cardVariant = {
    hidden: { opacity: 0, y: 40 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Why() {
    const { t } = useI18n();
    const s = t.why;
    return (
        <section
            id="why"
            className="section relative bg-[#050505]"
            data-testid="why-section"
        >
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-24">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[1.02]">
                            {s.titlePre}
                            <span className="italic font-extralight text-white/60">
                                {s.titleAccent}
                            </span>
                            {s.titlePost}
                        </h2>
                    </div>
                    <p className="font-body text-white/55 max-w-sm text-base leading-relaxed">
                        {s.lede}
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                    data-testid="why-grid"
                >
                    {s.cards.map((p, i) => {
                        const Icon = icons[i] || Film;
                        const n = String(i + 1).padStart(2, "0");
                        return (
                            <motion.article
                                key={n}
                                custom={i}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={cardVariant}
                                className="service-card group relative p-8 md:p-12 min-h-[320px] flex flex-col justify-between overflow-hidden"
                                data-testid={`why-card-${n}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-white/80 group-hover:text-[#E55B13] group-hover:border-[#E55B13]/40 transition-colors duration-500">
                                        <Icon size={22} strokeWidth={1.4} />
                                    </div>
                                    <span className="font-mono text-[11px] tracking-[0.28em] text-white/30">
                                        {n}
                                    </span>
                                </div>

                                <div className="mt-16">
                                    <h3 className="font-heading text-2xl md:text-3xl font-light tracking-[-0.02em] text-white leading-snug">
                                        {p.title}
                                    </h3>
                                    <p className="mt-4 font-body text-[15px] md:text-base text-white/60 leading-relaxed max-w-lg">
                                        {p.body}
                                    </p>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
